import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';

const DesignProcess = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const steps = [
    {
      title: 'Initial Consultation',
      description: 'We begin with a detailed discussion of your vision, needs, and budget to establish project goals.',
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
        </svg>
      ),
    },
    {
      title: '3D Design Development',
      description: 'Our expert designers create detailed 3D renderings, allowing you to visualize your project from every angle.',
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
        </svg>
      ),
    },
    {
      title: 'Design Refinement',
      description: 'We collaborate with you to refine the design, ensuring every detail meets your expectations before construction begins.',
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
        </svg>
      ),
    },
  ];

  return (
    <section id="design-process" className="section bg-white">
      <div className="container">
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
          transition={{ duration: 0.5 }}
          className="text-center mb-16"
        >
          <h2 className="section-title">Visualize Your Dream Space</h2>
          <p className="section-subtitle mx-auto">
            See your project come to life with our custom 3D design process, tailored specifically to your home and property.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={inView ? { opacity: 1, x: 0 } : { opacity: 0, x: -20 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="relative"
          >
            <div className="relative z-10 overflow-hidden rounded-lg shadow-xl">
              <img
                src="/images/3d-layers.png"
                alt="3D Design Visualization Process"
                className="w-full h-auto"
              />
            </div>
            <div className="absolute -bottom-6 -right-6 w-48 h-48 bg-primary/10 rounded-lg z-0"></div>
            <div className="absolute -top-6 -left-6 w-32 h-32 bg-accent/10 rounded-lg z-0"></div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={inView ? { opacity: 1, x: 0 } : { opacity: 0, x: 20 }}
            transition={{ duration: 0.5, delay: 0.4 }}
          >
            <h3 className="text-3xl font-bold mb-6">Your Vision, Perfectly Rendered</h3>
            <p className="text-gray-600 mb-8">
              Our program allows us to show you the full potential of your property, incorporating all the unique details and characteristics that make your house a home. With our 3D design process, you'll be able to visualize your outdoor living project and know you've made the right choice.
            </p>
            <div className="space-y-8">
              {steps.map((step, index) => (
                <div key={index} className="flex items-start">
                  <div className="flex-shrink-0 w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center text-primary">
                    {step.icon}
                  </div>
                  <div className="ml-4">
                    <h4 className="text-xl font-semibold mb-2">{step.title}</h4>
                    <p className="text-gray-600">{step.description}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-8 p-6 bg-light rounded-lg">
              <h4 className="text-xl font-semibold mb-3">Design with Confidence</h4>
              <p className="text-gray-600">
                Our 3D visualization process lets you see your dream outdoor environment before any ground work begins. We tailor every design to your specific house and surrounding terrain, ensuring the final result perfectly matches your vision and enhances your home's unique character.
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default DesignProcess; 